import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, TextField, InputAdornment, Chip, Accordion, AccordionSummary, AccordionDetails,
  Paper, Button, Card, CardContent,
} from '@mui/material';
import { Search, ExpandMore, Quiz, AutoAwesome, ArrowForward } from '@mui/icons-material';
import { preguntarAlAsistente } from '../components/AyudaModulo';

interface Faq {
  cat: string;
  p: string;
  simple: string;     // explicación lúdica, con analogía
  detalle: string;    // explicación concreta
  link?: { label: string; path: string };
}

const CATEGORIAS = [
  { id: 'inicio', label: '🚀 Primeros pasos' },
  { id: 'agente', label: '🤖 Mi agente' },
  { id: 'canales', label: '📱 WhatsApp y canales' },
  { id: 'clientes', label: '🤝 Clientes y ventas' },
  { id: 'plan', label: '💳 Plan y pagos' },
  { id: 'seguridad', label: '🔒 Datos y seguridad' },
];

const FAQS: Faq[] = [
  { cat: 'inicio', p: '¿Qué es tRespondo, en una frase?',
    simple: 'Es como contratar a un vendedor que nunca duerme, no se enferma y se aprende tu catálogo en 5 minutos. 🦸',
    detalle: 'Un agente de inteligencia artificial que responde a tus clientes por WhatsApp, Instagram, Messenger y tu web, con la información de tu negocio. Detecta a los interesados, agenda citas y te avisa cuando alguien quiere hablar contigo.' },
  { cat: 'inicio', p: '¿Por dónde empiezo?',
    simple: 'Como armar una mesa de IKEA: sigue las piezas en orden y en 15 minutos está lista. 🪑',
    detalle: '1) Completa Mi perfil. 2) En Mi Agente elige una plantilla de tu industria y reemplaza los [COMPLETAR]. 3) Pruébalo en el Playground. 4) Conecta tu WhatsApp. 5) Activa el agente.',
    link: { label: 'Ir a Mi perfil', path: '/app/perfil' } },
  { cat: 'inicio', p: '¿Necesito saber programar?',
    simple: 'Para nada. Si sabes mandar un audio por WhatsApp, sabes usar esto. 🎙️',
    detalle: 'Todo se configura con formularios y botones. Solo la conexión con la API oficial de Meta requiere copiar y pegar unos datos; el botón “Ayuda” de cada pantalla te guía paso a paso.' },
  { cat: 'agente', p: '¿Qué diferencia hay entre instrucciones y base de conocimiento?',
    simple: 'Las instrucciones son la personalidad del vendedor; la base es el catálogo que tiene en la mano. 📒',
    detalle: 'Instrucciones: cómo se comporta (“pide el nombre antes de cotizar”, “no des descuentos”). Base de conocimiento: lo que sabe (precios, horarios, políticas, preguntas frecuentes).',
    link: { label: 'Ir a Mi Agente', path: '/app/agente' } },
  { cat: 'agente', p: '¿El agente puede inventar cosas?',
    simple: 'Le enseñamos a decir “déjame confirmarlo” en vez de improvisar, como un buen practicante. 🙋',
    detalle: 'Está instruido para no inventar datos: si algo no está en tu base, ofrece derivar a un humano. Igual conviene revisar las conversaciones al principio y agregar correcciones en Soporte → Entrenamiento.' },
  { cat: 'agente', p: '¿Cómo hago que responda mejor?',
    simple: 'Igual que a un empleado nuevo: mientras mejor lo capacitas, mejor atiende. 🎓',
    detalle: 'Usa “Analizar con IA” en la base de conocimiento: te da un puntaje, te dice qué falta y te sugiere textos listos para agregar. Luego “Mejorar con IA” ordena tus instrucciones. Por último, prueba con las preguntas sugeridas en el Playground.' },
  { cat: 'agente', p: '¿Qué son los [COMPLETAR] de las plantillas?',
    simple: 'Son los espacios en blanco de un formulario: la plantilla trae la estructura y tú pones tus datos. ✍️',
    detalle: 'Reemplázalos por tu información real (precios, comunas, plazos) antes de activar el agente. Si quedan, la plataforma te avisa con un mensaje amarillo.' },
  { cat: 'agente', p: '¿Puedo subir varios PDF?',
    simple: 'Sí, como ir sumando hojas a una carpeta. 📂',
    detalle: 'Al subir un archivo elige “Agregar al final”: se suma con el nombre del documento como título. La IA lee hasta 15.000 caracteres en cada respuesta; deja lo más importante arriba.' },
  { cat: 'canales', p: '¿QR o API oficial de WhatsApp?',
    simple: 'El QR es la bicicleta: partes hoy. La API oficial es el auto: más estable para recorrer largo. 🚲🚗',
    detalle: 'QR: rápido para probar, usa tu WhatsApp normal. API oficial (Meta): recomendada para uso comercial, permite plantillas y no depende de tu teléfono.',
    link: { label: 'Conectar WhatsApp', path: '/app/conexion' } },
  { cat: 'canales', p: '¿Por qué no puedo escribirle a un cliente después de 24 horas?',
    simple: 'Es la regla de Meta para evitar spam: pasadas 24 h, solo puedes “tocar la puerta” con una plantilla aprobada. 🚪',
    detalle: 'En WhatsApp API, Instagram y Messenger, después de 24 h desde el último mensaje del cliente solo se permiten plantillas aprobadas por Meta.',
    link: { label: 'Ver plantillas', path: '/app/plantillas' } },
  { cat: 'canales', p: '¿Puedo poner el chat en mi página web?',
    simple: 'Sí: copias una línea de código y aparece la burbuja verde en tu web, como pegar un sticker. 🟢',
    detalle: 'En Soporte → Widget eliges color y textos y copias el código. Pégalo en tu sitio antes de </body>. Usa el mismo agente y base de conocimiento.',
    link: { label: 'Configurar widget', path: '/app/soporte' } },
  { cat: 'clientes', p: '¿Cómo respondo yo a un cliente?',
    simple: 'Tomas el volante: el piloto automático se aparta hasta que se lo devuelvas. 🧑‍✈️',
    detalle: 'En Conversaciones abre el chat y escribe abajo. El bot se pausa en esa conversación. Cuando termines, pulsa “Devolver al bot”.',
    link: { label: 'Ir a Conversaciones', path: '/app/conversaciones' } },
  { cat: 'clientes', p: '¿Qué es un lead?',
    simple: 'Es el cliente que pasó de “solo miro” a “¿cuánto sale?”. 👀➡️💰',
    detalle: 'La IA califica cada conversación de 0 a 100 según la intención de compra. Desde 40 se crea un lead, se agrega al Pipeline y, si lo tienes conectado, a Google Sheets o Slack.' },
  { cat: 'clientes', p: '¿El agente puede agendar citas?',
    simple: 'Sí, como una secretaria que anota directo en tu agenda de Google. 📅',
    detalle: 'Conecta Google Calendar en Integraciones y activa “Agendar” en Orquestación. También puedes usar el bloque “Agendar cita” en Flujos.',
    link: { label: 'Ir a Orquestación', path: '/app/orquestacion' } },
  { cat: 'plan', p: '¿Qué pasa cuando termina la prueba gratis?',
    simple: 'El vendedor se toma una pausa hasta que firmes su contrato; no se lleva nada. ⏸️',
    detalle: 'El agente deja de responder, pero tu configuración, conversaciones y leads se mantienen. Al contratar un plan vuelve a funcionar al instante.',
    link: { label: 'Ver planes', path: '/app/suscripcion' } },
  { cat: 'plan', p: '¿Puedo cancelar cuando quiera?',
    simple: 'Sí, sin letra chica: es como no renovar el gimnasio. 🏋️',
    detalle: 'En Suscripción pulsa “Cancelar plan”. Tu agente sigue funcionando hasta el final del periodo pagado y puedes reanudar antes de esa fecha.' },
  { cat: 'plan', p: '¿Qué es el saldo prepago?',
    simple: 'Como la carga de la BIP: se usa solo si te pasas del límite de tu plan. 🚌',
    detalle: 'Si tu plan tiene un límite de respuestas al mes, las respuestas extra se descuentan de tu saldo. Lo recargas en Suscripción.' },
  { cat: 'plan', p: '¿Qué pasa si falla el pago automático?',
    simple: 'Te avisamos y lo intentamos de nuevo, como el cartero que vuelve al día siguiente. 📬',
    detalle: 'Reintentamos hasta 3 veces (una por día) y te enviamos un correo. Además tienes 3 días de gracia en que el agente sigue respondiendo.' },
  { cat: 'seguridad', p: '¿Quién puede ver mis conversaciones?',
    simple: 'Solo tú. Si soporte necesita revisar algo, queda registrado, como la firma en un libro de visitas. 📖',
    detalle: 'Cada cuenta ve solo sus datos. Los administradores pueden revisar conversaciones en modo lectura para dar soporte, y cada acceso queda auditado.' },
  { cat: 'seguridad', p: '¿Mis datos entrenan a otras IA?',
    simple: 'No. Tu información es tuya y se usa solo para responder a tus clientes. 🔐',
    detalle: 'Usamos modelos en AWS Bedrock, que no usan tus datos para entrenar modelos. Tu base de conocimiento solo se envía para generar las respuestas de tu agente.' },
];

const PreguntasFrecuentes: React.FC = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [abierta, setAbierta] = useState<string | false>(false);

  const lista = useMemo(() => {
    const n = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const t = n(q.trim());
    return FAQS.filter((f) => (!cat || f.cat === cat) && (!t || n(`${f.p} ${f.simple} ${f.detalle}`).includes(t)));
  }, [q, cat]);

  return (
    <Box>
      <Paper sx={{ p: { xs: 3, md: 4 }, mb: 3, borderRadius: 3, color: '#fff', background: 'linear-gradient(135deg,#075E54 0%,#128C7E 55%,#25D366 100%)' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Quiz />
          <Typography variant="h4" sx={{ fontWeight: 800 }}>Preguntas frecuentes</Typography>
        </Stack>
        <Typography sx={{ mt: 1, opacity: 0.95, maxWidth: 640 }}>
          Explicado en simple, como se lo contarías a un amigo en un café ☕. Y si no está aquí, el asistente te responde al tiro.
        </Typography>
        <TextField
          fullWidth placeholder="Busca: cancelar, QR, lead, PDF, 24 horas…" value={q} onChange={(e) => setQ(e.target.value)}
          sx={{ mt: 2, maxWidth: 640, bgcolor: '#fff', borderRadius: 2, '& fieldset': { border: 'none' } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />
      </Paper>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Chip label="Todas" color={!cat ? 'primary' : 'default'} variant={!cat ? 'filled' : 'outlined'} onClick={() => setCat('')} />
        {CATEGORIAS.map((c) => (
          <Chip key={c.id} label={c.label} color={cat === c.id ? 'primary' : 'default'} variant={cat === c.id ? 'filled' : 'outlined'} onClick={() => setCat(c.id)} />
        ))}
      </Stack>

      {lista.map((f) => (
        <Accordion key={f.p} expanded={abierta === f.p} onChange={(_, v) => setAbierta(v ? f.p : false)} disableGutters
          sx={{ mb: 1, borderRadius: '12px !important', border: '1px solid #E2E8F0', boxShadow: 'none', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography sx={{ fontWeight: 600 }}>{f.p}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ p: 1.5, mb: 1.5, borderRadius: 2, bgcolor: '#E8F8EE', borderLeft: '4px solid #25D366' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#075E54' }}>EN SIMPLE</Typography>
              <Typography variant="body1">{f.simple}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">{f.detalle}</Typography>
            {f.link && <Button size="small" endIcon={<ArrowForward />} sx={{ mt: 1 }} onClick={() => navigate(f.link!.path)}>{f.link.label}</Button>}
          </AccordionDetails>
        </Accordion>
      ))}

      {lista.length === 0 && (
        <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>No encontramos esa pregunta 🤔. Prueba con otra palabra o pregúntale al asistente.</Typography>
      )}

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6">¿Te quedó alguna duda?</Typography>
              <Typography variant="body2" color="text.secondary">El asistente de ayuda conoce toda la plataforma y responde en segundos.</Typography>
            </Box>
            <Button variant="contained" startIcon={<AutoAwesome />} onClick={() => preguntarAlAsistente(q ? `Tengo una duda sobre: ${q}` : '¿Qué me recomiendas configurar primero?')}>
              Preguntar al asistente
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PreguntasFrecuentes;
